import React, { useEffect } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { object, string } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { setLanguage } from "../../../redux/slices/languageSlice";
import { Alert, Box, Button, CircularProgress, TextField, Typography } from "@mui/material";
import { authenticateUser } from "../slices/authSlice";
import { useNavigate } from "react-router-dom";
import clientRoutes from "../../../constants/clientRoutes";

const useValidationSchema = () => {
  const { t } = useTranslation();
  return object({
    username: string().min(1, t("login.validation.usernameRequired")),
    password: string().min(1, t("login.validation.passwordRequired")),
  });
};

interface LoginFormInput {
  username: string;
  password: string;
}

const LoginForm = () => {
  const navigate = useNavigate();
  const validationSchema = useValidationSchema();
  const { control, handleSubmit, formState: { errors } } = useForm<LoginFormInput>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      username: "",
      password: "",
    },
  });

  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const direction = useAppSelector((state) => state.languageSlice.direction);
  const authState = useAppSelector((state) => state.authSlice);

  const onSubmit: SubmitHandler<LoginFormInput> = async (data) => {
    try {
      await dispatch(authenticateUser(data)).unwrap();
      navigate("/");
    } catch (error) {
      console.error("Authentication failed:", error);
    }
  };

  useEffect(() => {
    if (authState.isLogged) {
      navigate(clientRoutes.INFOS_CLIENT);
    }
  }, [authState.isLogged, navigate]);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    dispatch(setLanguage(lng));
  };

  return (
    <Box
      m={2}
      p={4}
      borderRadius={5}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        maxWidth: {
          xs: "90%",
          sm: "90%",
          md: "90%",
          lg: "80%",
          xl: "80%",
        },
        border: "1px solid #E4E4EA",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
        position: 'relative',
        left: { xs: '0', sm: '0', md: '0', lg: '-150px' },
        top: { xs: '-100px', sm: '-100px', md: '-100px', lg: '0' },
        backgroundColor: '#fff'
      }}
      dir={direction}
    >
      <Typography sx={{ color: "#182250" }} variant="h4" component="h3">
        {t("login.title")}
      </Typography>
      <Typography sx={{ color: "#000" }} variant="caption">
        {t("login.subTitile")}
      </Typography>

      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        {authState.error && (
          <Alert severity="error">
            {typeof authState.error === "string"
              ? authState.error
              :  "Erreur serveur"}
          </Alert>
        )}

        <Controller
          name="username"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              margin="normal"
              fullWidth
              dir={direction}
              label={t("login.username")}
              error={!!errors.username}
              helperText={errors.username ? errors.username.message : ""}
            />
          )}
        />
        <Controller
          name="password"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              sx={{ marginBottom: '4%' }}
              margin="normal"
              fullWidth
              dir={direction}
              label={t("login.password")}
              type="password"
              error={!!errors.password}
              helperText={errors.password ? errors.password.message : ""}
            />
          )}
        />
        <Button type="submit" fullWidth variant="contained" disabled={authState.loading}>
          {authState.loading ? <CircularProgress size={24} /> : t("login.submit")}
        </Button>

        <Box
          mt={2}
          dir="ltr"
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
          }}
        >
          <Typography
            onClick={() => changeLanguage("fr")}
            sx={{ color: "#000", cursor: 'pointer' }}
            variant="caption"
          >
            Francais
          </Typography>
          <div> - </div>
          <Typography
            onClick={() => changeLanguage("ar")}
            sx={{ color: "#000", cursor: 'pointer' }}
            variant="caption"
          >
            العربية
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginForm;
